import { IWaterSource } from '@/api/config/waterSourceApi'
import { useMemo } from 'react'
import useSearchWaterSource from './useSearchWaterSource'

export interface WaterSourceUsage {
  /** Flow sensor id -> names of the water sources metering through it. */
  sensorUsedBy: Map<number, string[]>
  /** Flow valve id -> names of the water sources opening through it. */
  valveUsedBy: Map<number, string[]>
  /**
   * True once the list has been read and is not being re-read. Until then the
   * maps are empty, which says "nothing uses it" about every device — a
   * delete has to wait for this rather than trust them (SCRUM-1032 review).
   */
  ready: boolean
  /** Set when the list could not be read, so a delete can say why it waits. */
  error: unknown
}

const NO_USAGE = {
  sensorUsedBy: new Map<number, string[]>(),
  valveUsedBy: new Map<number, string[]>(),
}

/**
 * Which water sources reference each flow sensor and flow valve on the
 * interface. A referenced device cannot be deleted - the water source would
 * lose the IO it meters or shuts off - so the delete paths ask here first and
 * can name the water sources standing in the way.
 *
 * The check has to cover every water source, not the first page of them, so it
 * asks for a page big enough to hold them all. `PageSize: 0` - the "whole
 * list" idiom the other endpoints take - answers 500 here, and a failed query
 * would leave the maps empty and let a used device through.
 */
const useWaterSourceUsage = (interfaceId: number): WaterSourceUsage => {
  const { data, isSuccess, isFetching, error } = useSearchWaterSource(
    { PageSize: 1000, PageIndex: 1 },
    interfaceId,
  )
  const ready = isSuccess && !isFetching

  const maps = useMemo(() => {
    const list: IWaterSource[] = Array.isArray(data?.list) ? data!.list : []
    if (!list.length) return NO_USAGE

    const sensorUsedBy = new Map<number, string[]>()
    const valveUsedBy = new Map<number, string[]>()

    const add = (map: Map<number, string[]>, id: number, name: string) => {
      if (!id) return
      map.set(id, [...(map.get(id) ?? []), name])
    }

    list.forEach((source) => {
      add(sensorUsedBy, Number(source.flowSensorId), source.name)
      add(valveUsedBy, Number(source.flowValveId), source.name)
    })

    return { sensorUsedBy, valveUsedBy }
  }, [data])

  return useMemo(
    () => ({ ...maps, ready, error: ready ? null : error }),
    [maps, ready, error],
  )
}

export default useWaterSourceUsage
